// Manifest loader — DECISION-024.
//
// Two sources, in priority order:
//   1. Inline in boot.json.fixtureCatalogue[kind].manifest (rare;
//      Forge would compose manifest directly into boot.json)
//   2. Sidecar via boot.json.fixtureCatalogue[kind].manifestAssetKey
//      (canonical path; Forge G-2 publishes <kind>.manifest.json
//      alongside <kind>.glb to CDN, Runtime fetches)
//   3. null — no manifest available; composer returns null; stage.js
//      falls back to legacy per-kind builder if one exists, else the
//      anchor renders nothing
//
// NO BUNDLED FALLBACK (Shaun directive 2026-05-28: "remove the
// fallback, easier to spot the fails"). If CDN is unreachable or the
// manifest doesn't match the schema-shape guard, the failure is
// visible — the kind doesn't render — rather than silently masked
// by a bundled copy that may drift from the CDN authoring source.

const CDN_BASE = (() => {
	try {
		const raw = import.meta.env.VITE_SCENE_CDN_URL;
		if (typeof raw === "string" && raw.length > 0) return raw.replace(/\/$/, "");
	} catch { /* not under Vite — fall through */ }
	return null;
})();

const CACHE = new Map();

/**
 * Resolve the manifest for a fixture-kind from boot.json's catalogue
 * (inline or CDN sidecar). Returns the manifest object (or a Promise
 * of it) or null when no manifest is available — no bundled fallback.
 *
 * @param {Object} catalogue boot.json.fixtureCatalogue
 * @param {string} kind
 * @returns {Promise<Object | null>}
 */
export async function loadFixtureManifest({ catalogue, kind }) {
	const entry = catalogue?.[kind];

	// Schema-shape guard. CDN/inline manifests are only consumed when they match Runtime's accepted
	// window:
	//   - schemaVersion: 2 (merged shape from coord PR #142 reconciliation, release-2026-05-27-06)
	//   - schemaVersion: 3 (adds displayVariants[] + count envelopes per slot; AAA Engine
	//                       Foundation milestone refinement)
	//   - schemaVersion: 4 (ASK-106 attachment grammar — adds the `attachment` block and retires
	//                       `validSurfaces`/`orientationOnSurface`/`requiredArea`/`minClearance`
	//                       from `placementRules`; the composer reads NONE of those, they are
	//                       editor-side, so v4 is additive for everything this loader feeds)
	//   - kind field (string)
	// Transitional acceptance window per Shaun D-1 2026-05-28 so Content can re-author manifests
	// per-kind without a hard cutover.
	//
	// ⚠️ WHY v4 IS HERE, recorded because it cost a live bake: Content released the boot pin to the
	// v4 tag on 2026-08-20 while this window still read {2,3}. Every one of the 42 v4 manifests was
	// rejected here, the composer returned null for each, and — exactly as D-1 ratifies ("visible
	// failures preferred over silent masking") — the shop rendered **49 meshes against a healthy
	// 311**. Auto-rollback restored the old pin. The window is the thing that has to move FIRST:
	// a schema bump cannot be pinned before its consumer can read it (ASK-106, spec §5).
	const ACCEPTED_SCHEMA_VERSIONS = [2, 3, 4];
	const isRuntimeCompatible = (m) =>
		m && typeof m === "object"
		&& ACCEPTED_SCHEMA_VERSIONS.includes(m.schemaVersion)
		&& typeof m.kind === "string";
	const expected = `schemaVersion ∈ {${ACCEPTED_SCHEMA_VERSIONS.join(", ")}}`;

	// Source 1: inline manifest baked into boot.json (Forge G-2 future).
	if (entry?.manifest && typeof entry.manifest === "object") {
		if (isRuntimeCompatible(entry.manifest)) {
			const cacheKey = `inline:${kind}`;
			if (!CACHE.has(cacheKey)) CACHE.set(cacheKey, Promise.resolve(entry.manifest));
			return CACHE.get(cacheKey);
		}
		console.warn(`[manifest-loader] ${kind}: inline manifest schema mismatch (expected ${expected} + kind field; got schemaVersion=${entry.manifest.schemaVersion} kind=${JSON.stringify(entry.manifest.kind)}); nothing composes for this kind`);
	}

	// Source 2: CDN sidecar via manifestAssetKey (Forge G-2 future).
	const key = entry?.manifestAssetKey;
	if (typeof key === "string" && key.length > 0 && CDN_BASE) {
		const url = `${CDN_BASE}/${key}`;
		let pending = CACHE.get(url);
		if (!pending) {
			pending = (async () => {
				try {
					const r = await fetch(url, { cache: "default" });
					if (!r.ok) throw new Error(`HTTP ${r.status}`);
					const manifest = await r.json();
					if (!isRuntimeCompatible(manifest)) {
						console.warn(`[manifest-loader] ${kind}: CDN manifest at ${url} doesn't match Runtime schema (expected ${expected} + kind field; got schemaVersion=${JSON.stringify(manifest?.schemaVersion)} kind=${JSON.stringify(manifest?.kind)}) — nothing composes for this kind.`);
						return null;
					}
					console.log(`[manifest-loader] loaded ${kind} from ${url}`);
					return manifest;
				} catch (err) {
					console.warn(`[manifest-loader] failed to load ${kind} from ${url}:`, err);
					return null;
				}
			})();
			CACHE.set(url, pending);
		}
		return pending;
	}

	// Nothing available — composer returns null; legacy renderer (if
	// any) serves; otherwise the anchor renders nothing (visible
	// failure per D-1 directive — easier to spot than silent
	// bundled-fallback success).
	return null;
}

export function _resetManifestCache() {
	CACHE.clear();
}
